import { API_BASE } from "./config/api";
import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Alert,
  Descriptions,
  Typography,
  Select,
  Space,
  Form,
  Input
} from "antd";
import axios from "axios";

const { Text } = Typography;

export default function RtaPasswordsModal({ open, onClose }) {
  const [arns, setArns] = useState([]);
  const [selectedArn, setSelectedArn] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [changeRta, setChangeRta] = useState("");
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState("");
  const [changeSuccess, setChangeSuccess] = useState("");
  const [form] = Form.useForm();

  const authHeaders = () => {
    const token = sessionStorage.getItem("distributor_auth_token") || "";
    return { "X-KG-Distributor-Authorization": `Bearer ${token}` };
  };

  const loadArnList = async () => {
    setError("");
    setData(null);
    setSelectedArn("");
    setListLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin_rta_passwords.php`, {
        headers: authHeaders()
      });
      if (res?.data?.status !== "ok" || !Array.isArray(res?.data?.arns)) {
        throw new Error("Invalid ARN list response.");
      }
      setArns(res.data.arns);
    } catch (e) {
      setArns([]);
      setError(e?.response?.data?.message || e?.message || "Unable to load ARN list.");
    } finally {
      setListLoading(false);
    }
  };

  const loadCredentials = async (arn) => {
    setSelectedArn(arn || "");
    setData(null);
    setError("");
    setChangeOpen(false);
    setChangeRta("");
    setChangeError("");
    setChangeSuccess("");
    form.resetFields();

    if (!arn) return;

    setCredentialLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin_rta_passwords.php`, {
        params: { arn },
        headers: authHeaders()
      });
      if (res?.data?.status !== "ok") {
        throw new Error(res?.data?.message || "Unable to load RTA credentials.");
      }
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Unable to load RTA credentials.");
    } finally {
      setCredentialLoading(false);
    }
  };

  const startChange = (rta) => {
    setChangeRta(rta);
    setChangeOpen(true);
    setChangeError("");
    setChangeSuccess("");
    form.resetFields();
  };

  const cancelChange = () => {
    setChangeOpen(false);
    setChangeRta("");
    setChangeError("");
    setChangeSuccess("");
    form.resetFields();
  };

  const submitPasswordChange = async (values) => {
    if (!selectedArn || !changeRta) return;

    setChangeLoading(true);
    setChangeError("");
    setChangeSuccess("");

    try {
      const res = await axios.post(
        `${API_BASE}/admin_rta_passwords.php`,
        {
          arn: selectedArn,
          rta: changeRta,
          old_password: values.old_password,
          new_password: values.new_password,
          confirm_new_password: values.confirm_new_password
        },
        {
          headers: {
            ...authHeaders(),
            "Content-Type": "application/json"
          }
        }
      );

      if (res?.data?.status !== "ok") {
        throw new Error(res?.data?.message || "Password change failed.");
      }

      setChangeSuccess(res?.data?.message || `${changeRta} password changed successfully.`);
      form.resetFields();

      // Refresh displayed credential only after a successful server-side update.
      const refreshed = await axios.get(`${API_BASE}/admin_rta_passwords.php`, {
        params: { arn: selectedArn },
        headers: authHeaders()
      });
      if (refreshed?.data?.status === "ok") {
        setData(refreshed.data);
      }
    } catch (e) {
      setChangeError(
        e?.response?.data?.message ||
        e?.message ||
        "Unable to change password."
      );
    } finally {
      setChangeLoading(false);
    }
  };

  const close = () => {
    setArns([]);
    setSelectedArn("");
    setData(null);
    setError("");
    setListLoading(false);
    setCredentialLoading(false);
    setChangeOpen(false);
    setChangeRta("");
    setChangeError("");
    setChangeSuccess("");
    setChangeLoading(false);
    form.resetFields();
    onClose?.();
  };

  useEffect(() => {
    if (open) loadArnList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = arns.map((item) => ({
    value: item.arn_number,
    label: item.arn_holder_name
      ? `${item.arn_number} — ${item.arn_holder_name}`
      : item.arn_number
  }));

  return (
    <Modal
      title="RTA File Credentials"
      open={open}
      onCancel={close}
      footer={<Button onClick={close}>Close</Button>}
      destroyOnClose
      maskClosable={false}
      width={700}
    >
      <Alert
        type="warning"
        showIcon
        message="Admin-only confidential information"
        description="Select an ARN to view or change its CAMS and KFin file credentials."
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
        <Text strong>Select ARN</Text>
        <Select
          showSearch
          allowClear
          value={selectedArn || undefined}
          placeholder={listLoading ? "Loading ARNs..." : "Select ARN"}
          loading={listLoading}
          disabled={listLoading || changeLoading}
          options={options}
          onChange={loadCredentials}
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label || "").toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: "100%" }}
        />
      </Space>

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      )}

      {credentialLoading && <Text>Loading secure credentials...</Text>}

      {!credentialLoading && data && (
        <>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ARN">{data.arn_number || "—"}</Descriptions.Item>
            <Descriptions.Item label="ARN Holder">{data.arn_holder_name || "—"}</Descriptions.Item>

            <Descriptions.Item label="CAMS ID">{data.cams_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="CAMS Password">
              <Space wrap>
                <Text copyable>{data.cams_password || "—"}</Text>
                <Button size="small" onClick={() => startChange("CAMS")}>
                  Change
                </Button>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label="KFin ID">{data.kfin_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="KFin Password">
              <Space wrap>
                <Text copyable>{data.kfin_password || "—"}</Text>
                <Button size="small" onClick={() => startChange("KFIN")}>
                  Change
                </Button>
              </Space>
            </Descriptions.Item>
          </Descriptions>

          {changeOpen && (
            <div
              style={{
                marginTop: 16,
                padding: 16,
                border: "1px solid #d9d9d9",
                borderRadius: 8
              }}
            >
              <Text strong>Change {changeRta} Password</Text>

              {changeError && (
                <Alert
                  type="error"
                  showIcon
                  message={changeError}
                  style={{ marginTop: 12, marginBottom: 12 }}
                />
              )}

              {changeSuccess && (
                <Alert
                  type="success"
                  showIcon
                  message={changeSuccess}
                  style={{ marginTop: 12, marginBottom: 12 }}
                />
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={submitPasswordChange}
                autoComplete="off"
                style={{ marginTop: 12 }}
              >
                <Form.Item
                  label="Old Password"
                  name="old_password"
                  rules={[{ required: true, message: "Enter the current password." }]}
                >
                  <Input.Password
                    autoComplete="current-password"
                    placeholder="Enter current password"
                  />
                </Form.Item>

                <Form.Item
                  label="New Password"
                  name="new_password"
                  rules={[{ required: true, message: "Enter the new password." }]}
                >
                  <Input.Password
                    autoComplete="new-password"
                    placeholder="Enter new password"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm New Password"
                  name="confirm_new_password"
                  dependencies={["new_password"]}
                  rules={[
                    { required: true, message: "Confirm the new password." },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("new_password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("New passwords do not match."));
                      }
                    })
                  ]}
                >
                  <Input.Password
                    autoComplete="new-password"
                    placeholder="Re-enter new password"
                  />
                </Form.Item>

                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={changeLoading}
                  >
                    Change Password
                  </Button>
                  <Button
                    onClick={cancelChange}
                    disabled={changeLoading}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form>
            </div>
          )}
        </>
      )}

      {!listLoading && !credentialLoading && !data && !error && (
        <Text type="secondary">Choose an ARN from the list above.</Text>
      )}
    </Modal>
  );
}
